/**
 * Where a website submission belongs, decided by its form name. This MUST stay
 * in step with the database trigger (migration 075, assign_quarantine_ref):
 * the trigger mints the reference by the same rules, so a row the CRM shows on
 * the Enquiry queue is exactly a row that drew a TC-ENQ number.
 *
 *   referral    -> Referrals page   (TC-REF-YYYY-NNNNN)
 *   enquiry     -> Enquiry queue    (TC-ENQ-YYYY-NNNNN)
 *   recruitment -> Applicants queue (TC-APP / TC-TAL / TC-EOI -YYYY-NNNNN --
 *                  one queue, but a code per careers form so the reference
 *                  says whether it was an application, a talent-pool entry or
 *                  an expression of interest)
 *   other       -> Website inbox    (no reference)
 *
 * Change a rule here and in the migration together, never one alone: they are
 * the same decision made twice, and drift would put a row on the wrong page or
 * give it the wrong number.
 */
export type IntakeStream = "referral" | "enquiry" | "recruitment" | "other";

export interface QuarantineRow {
    id: string;
    ref_code: string | null;
    /**
     * Set only on rows renumbered by migration 077, which brought the old
     * TC-ENQ-DDMMYYYY-NNNNN codes onto the current shape. Shown in the queues
     * so a person quoting their original number can still be matched.
     */
    previous_ref_code?: string | null;
    payload: Record<string, unknown>;
    source?: string;
    form_name: string | null;
    received_at: string;
}

/**
 * Whether a submission is the website's feedback / complaint / compliment
 * form. These still live on the Website inbox rather than a queue of their
 * own, but they can be promoted into the feedback register, so the inbox needs
 * to recognise them. Kept separate from streamOf on purpose: this decides an
 * ACTION, not which page the row appears on.
 */
export const isFeedbackForm = (formName: string | null | undefined): boolean => {
    const f = (formName ?? "").toLowerCase();
    return f.includes("feedback") || f.includes("complaint") || f.includes("compliment");
};

/**
 * Which of feedback / complaint / compliment a submission is, taken from the
 * form name so the register mints the matching TC-FBK / TC-CML / TC-CMP.
 */
export const feedbackTypeOf = (formName: string | null | undefined): "feedback" | "complaint" | "compliment" => {
    const f = (formName ?? "").toLowerCase();
    if (f.includes("complaint")) return "complaint";
    if (f.includes("compliment")) return "compliment";
    return "feedback";
};

export const streamOf = (formName: string | null | undefined): IntakeStream => {
    const f = (formName ?? "").toLowerCase();
    if (!f || f.includes("referral")) return "referral";
    if (f.includes("contact") || f.includes("callback")) return "enquiry";
    // Matched broadly so the website can name these talent-pool, job-apply,
    // job-application, expression-of-interest, eoi or careers.
    if (f.includes("talent") || f.includes("job") || f.includes("career")
        || f.includes("expression") || f.includes("eoi")) return "recruitment";
    return "other";
};
